import type { Feature, FeatureCollection, Point, Polygon } from 'geojson';
import type { WorkspaceResearchData } from '../command-workspace-contract';
import type { Bbox, Source, Summary } from '../fire-signals/contract';
import { parseBbox } from '../fire-signals/contract';
import { geoScope, inBounds, STATION_METADATA, utc } from './config';

export interface GeoProperties {
  id: string;
  kind: 'NASA_HISTORICAL' | 'NOAA_STATION' | 'STUDY_SELECTION';
  sourceAuthority: string;
  timestamp: string | null;
  rawRef: string;
  title: string;
}
export type GeoPoint = Feature<Point, GeoProperties>;
export function researchAreaBounds(areas: Summary['areas'], areaRef: string): Bbox | null {
  const bounds = areas
    .filter((area) => !areaRef || area.areaRef === areaRef)
    .map((area) => parseBbox(area.bbox.join(',')));
  if (!bounds.length || bounds.some((box) => !box || box[0] >= box[2])) return null;
  const boxes = bounds as Bbox[];
  return [
    Math.min(...boxes.map((box) => box[0])),
    Math.min(...boxes.map((box) => box[1])),
    Math.max(...boxes.map((box) => box[2])),
    Math.max(...boxes.map((box) => box[3])),
  ];
}
export interface GeoProjection {
  nasa: FeatureCollection<Point, GeoProperties>;
  noaa: FeatureCollection<Point, GeoProperties>;
  boundary: FeatureCollection<Polygon, GeoProperties>;
  rejected: number;
  duplicates: number;
  scopeValid: boolean;
}
export function selectedStationLabel(
  geo: GeoProjection,
  id: string,
  historical: boolean,
  layers: readonly string[],
): string | null {
  if (!historical || !layers.includes('noaa')) return null;
  const feature = geo.noaa.features.find((feature) => feature.properties.id === id);
  return feature
    ? `${feature.properties.title} · ${feature.geometry.coordinates.join(', ')}`
    : null;
}
export function coordinates(lon: unknown, lat: unknown): lon is number {
  return (
    typeof lon === 'number' &&
    typeof lat === 'number' &&
    Number.isFinite(lon) &&
    Number.isFinite(lat) &&
    Math.abs(lon) <= 180 &&
    Math.abs(lat) <= 90
  );
}
const hash = (v: string) => /^[a-f0-9]{64}$/.test(v);
function sourceBound(
  sources: Source[],
  revision: string,
  uri: string,
  authority: 'nasa' | 'noaa',
): boolean {
  try {
    const url = new URL(uri);
    const host = authority === 'nasa' ? 'firms.modaps.eosdis.nasa.gov' : 'www.ncei.noaa.gov';
    return (
      url.protocol === 'https:' &&
      url.hostname === host &&
      !url.username &&
      !url.password &&
      hash(revision) &&
      sources.some(
        (s) =>
          s.originalHash === revision &&
          s.sourceRevision === revision &&
          s.originalUri === uri &&
          (authority === 'nasa' ? s.provider === 'NASA_FIRMS' : s.provider.startsWith('NOAA_')),
      )
    );
  } catch {
    return false;
  }
}
/** Validates immutable API identities; never repairs or guesses a coordinate. */
export function toGeoJSON(
  data: WorkspaceResearchData | null,
  search: string,
  visibleIds?: ReadonlySet<string>,
): GeoProjection {
  const result: GeoProjection = {
    nasa: { type: 'FeatureCollection', features: [] },
    noaa: { type: 'FeatureCollection', features: [] },
    boundary: { type: 'FeatureCollection', features: [] },
    rejected: 0,
    duplicates: 0,
    scopeValid: true,
  };
  const scope = geoScope(search);
  result.scopeValid = scope.valid;
  if (!data || !scope.valid) return result;
  const seen = new Set<string>(),
    rawSeen = new Set<string>();
  const inside = (lon: number, lat: number) => !scope.box || inBounds(lon, lat, scope.box);
  for (const c of data.candidates.items) {
    const s = c.satellite;
    const area = data.summary.areas.find((a) => a.areaRef === c.areaRef);
    const valid =
      c.mode === 'HISTORICAL_RESEARCH' &&
      /^candidate-[a-f0-9]{64}$/.test(c.candidateId) &&
      /^nasa-firms-[a-f0-9]{64}$/.test(s.detectionId) &&
      s.provider === 'NASA_FIRMS' &&
      s.crsEPSG === 4326 &&
      coordinates(s.longitude, s.latitude) &&
      utc(s.acquiredAtUTC) &&
      hash(s.rawRowHash) &&
      s.originalHash === s.sourceRevision &&
      c.sourceRevision === s.sourceRevision &&
      Number.isSafeInteger(s.rawRowNumber) &&
      s.rawRowNumber > 0 &&
      sourceBound(data.sources.items, s.sourceRevision, s.originalUri, 'nasa') &&
      area &&
      parseBbox(area.bbox.join(',')) &&
      inBounds(s.longitude, s.latitude, area.bbox);
    if (!valid) {
      result.rejected++;
      continue;
    }
    const rawId = s.originalHash + ':' + s.rawRowHash;
    if (seen.has(c.candidateId) || rawSeen.has(rawId)) {
      result.duplicates++;
      continue;
    }
    seen.add(c.candidateId);
    rawSeen.add(rawId);
    if (
      (visibleIds && !visibleIds.has(c.candidateId)) ||
      !inside(s.longitude, s.latitude) ||
      (scope.from && Date.parse(s.acquiredAtUTC) < Date.parse(scope.from)) ||
      (scope.to && Date.parse(s.acquiredAtUTC) >= Date.parse(scope.to))
    )
      continue;
    result.nasa.features.push({
      type: 'Feature',
      id: c.candidateId,
      geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] },
      properties: {
        id: c.candidateId,
        kind: 'NASA_HISTORICAL',
        sourceAuthority: 'NASA_FIRMS',
        timestamp: s.acquiredAtUTC,
        rawRef: `${s.originalUri}#sha256=${s.originalHash}&row=${s.rawRowNumber}&rowHash=${s.rawRowHash}`,
        title: `${s.longitude}, ${s.latitude} · FRP ${s.frpMW ?? '未知'} MW · NOT_VERIFIED`,
      },
    });
  }
  const stations = new Set<string>();
  for (const s of data.summary.stations) {
    const expected = STATION_METADATA.coordinates[s.stationId];
    if (
      !coordinates(s.longitude, s.latitude) ||
      !expected ||
      expected[0] !== s.longitude ||
      expected[1] !== s.latitude ||
      s.sourceRevision !== STATION_METADATA.revision ||
      s.originalUri !== STATION_METADATA.uri
    ) {
      result.rejected++;
      continue;
    }
    if (stations.has(s.stationId)) {
      result.duplicates++;
      continue;
    }
    stations.add(s.stationId);
    if (!inside(s.longitude, s.latitude)) continue;
    result.noaa.features.push({
      type: 'Feature',
      id: s.stationId,
      geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] },
      properties: {
        id: s.stationId,
        kind: 'NOAA_STATION',
        sourceAuthority: 'NOAA_NCEI',
        timestamp: null,
        rawRef: `${s.originalUri}#sha256=${s.sourceRevision}&station=${s.stationId}`,
        title: `${s.name} · ${s.stationId} · 历史站点元数据（无观测时间）`,
      },
    });
  }
  for (const a of data.summary.areas) {
    const box = parseBbox(a.bbox.join(','));
    if (!box || box[0] >= box[2]) {
      result.rejected++;
      continue;
    }
    const [w, s, e, n] = box;
    result.boundary.features.push({
      type: 'Feature',
      id: a.areaRef,
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [w, s],
            [e, s],
            [e, n],
            [w, n],
            [w, s],
          ],
        ],
      },
      properties: {
        id: a.areaRef,
        kind: 'STUDY_SELECTION',
        sourceAuthority: 'VS07_RESEARCH_SELECTION_API',
        timestamp: null,
        rawRef: '/read-models/normal-monitoring/fire-signals/summary',
        title: `${a.name} · 研究选择范围，非行政或消防管辖界`,
      },
    });
  }
  return result;
}
